import { useState } from 'react';
import {
  Lock, Check, Mountain, Droplets, Bug, Building2, Satellite, Waves, TreePine,
  AlertCircle, Wrench, Clock, TrendingUp, TrendingDown, Zap, Loader2,
  Info, ChevronDown, ChevronRight, Network,
} from 'lucide-react';
import { useUIStore } from '../../stores/uiStore.js';
import { useDevelopmentDefinitions, useDevelopmentProjects, useStartProject, useCancelProject, useCompleteProject } from '../../hooks/useDevelopment.js';
import { useForecast } from '../../hooks/useDevelopment.js';
import { useProjectCountdown } from '../../hooks/useCountdown.js';
import type { DevelopmentDefinition } from '../../api/development.js';
import type { DevelopmentProject } from '../../api/development.js';
import ForecastModal from './ForecastModal.js';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  geology: <Mountain className="w-4 h-4" />,
  atmosphere: <Droplets className="w-4 h-4" />,
  water: <Waves className="w-4 h-4" />,
  life: <Bug className="w-4 h-4" />,
  plant: <TreePine className="w-4 h-4" />,
  civilisation: <Building2 className="w-4 h-4" />,
  technology: <Satellite className="w-4 h-4" />,
};

const TRADE_OFF_COLORS: Record<string, string> = {
  '+atmosphere': 'text-sky-400 bg-sky-950/50',
  '+water': 'text-blue-400 bg-blue-950/50',
  '+temperature': 'text-orange-400 bg-orange-950/50',
  '+stability': 'text-emerald-400 bg-emerald-950/50',
  '+biomass': 'text-green-400 bg-green-950/50',
  '+biodiversity': 'text-teal-400 bg-teal-950/50',
  '+primitiveLife': 'text-lime-400 bg-lime-950/50',
  '+oxygen': 'text-cyan-400 bg-cyan-950/50',
  '+population': 'text-amber-400 bg-amber-950/50',
  '+technology': 'text-violet-400 bg-violet-950/50',
  '+energy': 'text-yellow-400 bg-yellow-950/50',
  '+researchSpeed': 'text-indigo-300 bg-indigo-950/50',
  '-atmosphere': 'text-red-400 bg-red-950/50',
  '-water': 'text-red-400 bg-red-950/50',
  '-temperature': 'text-red-400 bg-red-950/50',
  '-stability': 'text-red-400 bg-red-950/50',
  '-energy': 'text-red-400 bg-red-950/50',
  '-researchSpeed': 'text-red-400 bg-red-950/50',
};

const STAGE_NAMES = [
  'Barren Planet',
  'Terraforming',
  'Spark of Life',
  'Plant Life',
  'Complex Life',
  'Civilisation',
  'Technology',
  'Space Technology',
  'Space Exploration',
];

type RequirementSource = 'stats' | 'lifeChance';

interface MilestoneRequirementDefinition {
  key: string;
  label: string;
  target: number;
  unit?: string;
  source?: RequirementSource;
}

interface MilestoneDefinition {
  stage: number;
  name: string;
  description: string;
  guidance: string;
  requirements: MilestoneRequirementDefinition[];
}

interface MilestoneRequirementProgress extends MilestoneRequirementDefinition {
  current: number;
  progress: number;
  complete: boolean;
}

interface NextMilestone {
  name: string;
  condition: string;
  guidance: string;
  progress: number;
  requirements: MilestoneRequirementProgress[];
  tooltipLines: string[];
}

const MILESTONES = [
  {
    stage: 1,
    name: 'Terraforming',
    description: 'Build a stable baseline of water and atmosphere.',
    guidance: 'Water and atmosphere projects move this bar.',
    requirements: [
      { key: 'water', label: 'Water', target: 10, unit: '%' },
      { key: 'atmosphere', label: 'Atmosphere', target: 10, unit: '%' },
    ],
  },
  {
    stage: 2,
    name: 'Spark of Life',
    description: 'Make conditions safe for Primordial Soup, then grow primitive life.',
    guidance: 'Terraforming work raises readiness; Primordial Soup finishes the stage push.',
    requirements: [
      { key: 'water', label: 'Water readiness', target: 30, unit: '%' },
      { key: 'atmosphere', label: 'Atmosphere readiness', target: 20, unit: '%' },
      { key: 'lifeChance', label: 'Life chance', target: 30, unit: '%', source: 'lifeChance' },
      { key: 'primitiveLife', label: 'Primitive life', target: 3, unit: '%' },
    ],
  },
  {
    stage: 3,
    name: 'Plant Life',
    description: 'Grow enough biomass for plants to spread.',
    guidance: 'Life and plant projects increase biomass.',
    requirements: [{ key: 'biomass', label: 'Biomass', target: 12, unit: '%' }],
  },
  {
    stage: 4,
    name: 'Complex Life',
    description: 'Support the first animal ecosystems.',
    guidance: 'Animal and habitat projects increase animal life.',
    requirements: [{ key: 'animalLife', label: 'Animal life', target: 2, unit: '%' }],
  },
  {
    stage: 5,
    name: 'Civilisation',
    description: 'Grow a stable population.',
    guidance: 'Settlement and food projects increase population.',
    requirements: [{ key: 'population', label: 'Population', target: 3, unit: '%' }],
  },
  {
    stage: 6,
    name: 'Technology',
    description: 'Grow an established civilisation that can support research.',
    guidance: 'Towns, schools, and engineering raise technology; settlements and farms grow population.',
    requirements: [
      { key: 'technology', label: 'Technology', target: 10, unit: '%' },
      { key: 'population', label: 'Population', target: 15, unit: '%' },
    ],
  },
  {
    stage: 7,
    name: 'Space Technology',
    description: 'Launch a Satellite, Signal Array, or Orbital Telescope to enter the space technology age.',
    guidance: 'Reach the technology level for a Satellite, then complete any orbital sensing project.',
    requirements: [
      { key: 'technology', label: 'Technology', target: 30, unit: '%' },
    ],
  },
  {
    stage: 8,
    name: 'Space Exploration',
    description: 'Finish Space Port and Rocket Program to open the space game.',
    guidance: 'Space Port needs a large population; Rocket Program needs technology and research.',
    requirements: [
      { key: 'technology', label: 'Technology', target: 45, unit: '%' },
      { key: 'researchSpeed', label: 'Research', target: 12, unit: '%' },
      { key: 'population', label: 'Population', target: 35, unit: '%' },
    ],
  },
] satisfies MilestoneDefinition[];

function formatRequirementValue(value: number, unit = '') {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}${unit}`;
}

function getRequirementCurrent(
  requirement: MilestoneRequirementDefinition,
  stats: Record<string, number>,
  lifeChance: number,
) {
  if (requirement.source === 'lifeChance') return lifeChance || 0;
  return stats[requirement.key] || 0;
}

export function getNextMilestone(stats: Record<string, number>, currentStage: number, lifeChance = 0): NextMilestone | null {
  const next = MILESTONES.find((m) => m.stage > currentStage);
  if (!next) return null;

  const requirements = next.requirements.map((requirement) => {
    const current = getRequirementCurrent(requirement, stats, lifeChance);
    const progress = Math.min(100, Math.max(0, Math.round((current / requirement.target) * 100)));
    return {
      ...requirement,
      current,
      progress,
      complete: current >= requirement.target,
    };
  });
  const progress = Math.round(
    requirements.reduce((total, requirement) => total + requirement.progress, 0) / requirements.length,
  );
  const requirementLines = requirements.map((requirement) => (
    `${requirement.label}: ${formatRequirementValue(requirement.current, requirement.unit)} / ${formatRequirementValue(requirement.target, requirement.unit)}`
  ));

  return {
    name: next.name,
    condition: next.description,
    guidance: next.guidance,
    progress,
    requirements,
    tooltipLines: [`Next stage: Stage ${next.stage} - ${next.name}`, next.description, ...requirementLines],
  };
}

interface DevelopmentPanelProps {
  currentBalance: number;
  stats?: Record<string, number>;
  stage?: number;
  lifeChance?: number;
}

function ActiveProjectCard({ project, onCancel, onComplete, dayDurationMs }: { project: DevelopmentProject; onCancel: (id: string) => void; onComplete: (id: string) => void; dayDurationMs?: number }) {
  const countdown = useProjectCountdown(project.started_at, project.total_construction_days, dayDurationMs);
  const isConstructing = project.status === 'constructing';
  const isActive = project.status === 'active';
  const canCancel = isConstructing && project.progress_days <= 0 && countdown.daysComplete < 1;

  return (
    <div className="p-2.5 rounded-lg bg-slate-800/60 border border-slate-700">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{project.upgrade?.name || 'Project'}</span>
        {isConstructing && countdown.isComplete && (
          <button
            onClick={(e) => { e.stopPropagation(); onComplete(project.project_id); }}
            className="text-[10px] bg-green-900/50 text-green-400 hover:text-green-300 px-1.5 py-0.5 rounded hover:bg-green-900/70 transition-colors"
          >
            Complete
          </button>
        )}
        {isConstructing && !countdown.isComplete && canCancel && (
          <button
            onClick={(e) => { e.stopPropagation(); onCancel(project.project_id); }}
            className="text-[10px] text-red-400 hover:text-red-300 px-1.5 py-0.5 rounded hover:bg-red-950/30 transition-colors"
          >
            Cancel (50% refund)
          </button>
        )}
        {isConstructing && !countdown.isComplete && !canCancel && (
          <span className="text-[10px] text-slate-500 px-1.5 py-0.5">
            Locked in
          </span>
        )}
        {isActive && (
          <button
            onClick={(e) => { e.stopPropagation(); onComplete(project.project_id); }}
            className="text-[10px] bg-green-900/50 text-green-400 hover:text-green-300 px-1.5 py-0.5 rounded hover:bg-green-900/70 transition-colors"
          >
            Complete ✓
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1.5">
        <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              countdown.isComplete ? 'bg-green-500' : 'bg-cyan-500'
            }`}
            style={{ width: `${Math.min(100, countdown.progressPercent)}%` }}
          />
        </div>
        <span className="text-[10px] text-slate-400 font-mono whitespace-nowrap">
          {countdown.isComplete ? 'Done' : countdown.formatted}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Clock className="w-3 h-3 text-slate-500" />
        <span className="text-[10px] text-slate-500">
          {isConstructing
            ? countdown.isComplete
              ? 'Construction complete - waiting for real-time tick'
              : `Building... ${Math.floor(countdown.daysComplete * 10) / 10}/${project.total_construction_days} days`
            : isActive
            ? 'Active - click Complete to free slot'
            : 'Completed'}
        </span>
      </div>
    </div>
  );
}

function getRequirementLines(
  def: DevelopmentDefinition,
  currentBalance: number,
  availableSlots: number,
  stage: number,
) {
  const lines: string[] = [];
  lines.push(`Stage required: ${def.stage_requirement} (current ${stage})`);
  lines.push(`Prerequisites: ${def.prerequisiteNames.length > 0 ? def.prerequisiteNames.join(', ') : 'None'}`);
  lines.push(`Stat requirements: ${def.unlockRequirements.length > 0 ? def.unlockRequirements.join(', ') : 'None'}`);

  const blockers: string[] = [];
  if (!def.unlocked && def.unlockReason) blockers.push(def.unlockReason);
  if (def.purchased) blockers.push('Already owned or under construction');
  if (def.hasConflict) blockers.push('Conflicts with an existing branch');
  if (currentBalance < def.cost) blockers.push(`Need ${def.cost - currentBalance} more credits`);
  if (availableSlots <= 0) blockers.push('No development slots free');

  lines.push(blockers.length > 0 ? `Blocked by: ${blockers.join(' / ')}` : 'Status: Available now');
  return lines;
}

export default function DevelopmentPanel({ currentBalance, stats, stage = 0, lifeChance = 0 }: DevelopmentPanelProps) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedUpgrade, setSelectedUpgrade] = useState<DevelopmentDefinition | null>(null);
  const [infoUpgradeId, setInfoUpgradeId] = useState<string | null>(null);
  const [activeProjectsCollapsed, setActiveProjectsCollapsed] = useState(false);
  const { data: defsData, isLoading: defsLoading } = useDevelopmentDefinitions();
  const { data: projectsData } = useDevelopmentProjects();
  const startProject = useStartProject();
  const cancelProject = useCancelProject();
  const completeProject = useCompleteProject();
  const { data: forecastData } = useForecast(selectedUpgrade?.upgrade_definition_id || null, 7);
  const { openTechTree, closeDevelopment } = useUIStore();

  const definitions = defsData?.data || [];
  const activeProjects = projectsData?.data?.active || [];
  const maxSlots = projectsData?.data?.maxSlots ?? 3;
  const availableSlots = projectsData?.data?.availableSlots ?? 3;
  const currentDay = projectsData?.data?.currentDay || 1;
  const dayDurationMs = projectsData?.data?.dayDurationMs;

  // Next milestone hint based on current stats
  const nextMilestone = getNextMilestone(stats || {}, stage, lifeChance);

  // Hide completed/purchased upgrades from the available grid
  const availableDefs = definitions.filter((d) => !d.purchased);
  const categories = ['all', ...new Set(availableDefs.map((u) => u.category))];

  const filteredDefs = activeCategory === 'all'
    ? availableDefs
    : availableDefs.filter((u) => u.category === activeCategory);

  const handleStart = (upgradeId: string) => {
    if (startProject.isPending) return;
    startProject.mutate(upgradeId);
    setSelectedUpgrade(null);
  };

  const handleCancel = (projectId: string) => {
    if (cancelProject.isPending) return;
    cancelProject.mutate(projectId);
  };

  const handleComplete = (projectId: string) => {
    if (completeProject.isPending) return;
    completeProject.mutate(projectId);
  };

  return (
    <div className="flex flex-col h-full" style={{ touchAction: 'pan-y' }}>
      {/* Header */}
      <div className="p-3 border-b border-slate-800 shrink-0 space-y-1.5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Wrench className="w-5 h-5 text-cyan-400" />
            Development
          </h2>
          <span className="text-xs text-slate-500 font-mono">Day {currentDay}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="group relative inline-flex">
            <span
              tabIndex={0}
              className="text-xs font-semibold text-cyan-300 bg-cyan-950/50 px-2 py-0.5 rounded-full border border-cyan-800/40 cursor-help focus:outline-none focus:ring-1 focus:ring-cyan-500/70"
            >
              Stage {stage}: {STAGE_NAMES[stage] || 'Unknown'}
            </span>
            {nextMilestone && (
              <div className="pointer-events-none absolute left-0 top-full z-40 mt-2 hidden w-64 rounded-lg border border-cyan-700 bg-slate-950 p-2 shadow-2xl shadow-black/70 group-hover:block group-focus-within:block">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-300">Next Stage Requirements</div>
                <div className="mt-1 text-xs font-semibold text-slate-100">{nextMilestone.name}</div>
                <div className="mt-1 space-y-0.5">
                  {nextMilestone.requirements.map((requirement) => (
                    <div key={requirement.key} className="flex items-center justify-between gap-2 text-[10px] leading-snug text-slate-300">
                      <span>{requirement.label}</span>
                      <span className={requirement.complete ? 'text-green-400' : 'text-slate-400'}>
                        {formatRequirementValue(requirement.current, requirement.unit)} / {formatRequirementValue(requirement.target, requirement.unit)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-1.5 text-[10px] leading-snug text-slate-500">{nextMilestone.guidance}</div>
              </div>
            )}
          </div>
        </div>
        {nextMilestone && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">Next: <span className="text-cyan-400 font-semibold">{nextMilestone.name}</span> <span className="text-slate-500 ml-1">{nextMilestone.progress}%</span></span>
              <span className="text-[10px] text-slate-500 truncate max-w-[50%]" title={nextMilestone.guidance}>{nextMilestone.guidance}</span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-cyan-500 rounded-full transition-all"
                style={{ width: `${nextMilestone.progress}%` }}
              />
            </div>
            <p className="text-[10px] text-slate-500">{nextMilestone.condition}</p>
          </div>
        )}
        {stage >= 6 && (
          <button
            onClick={() => {
              closeDevelopment();
              openTechTree();
            }}
            className="flex w-full min-h-9 items-center justify-between gap-2 rounded-lg border border-cyan-700/50 bg-cyan-950/30 px-3 py-1.5 text-left text-xs text-cyan-200 hover:bg-cyan-900/40 transition-colors"
            style={{ touchAction: 'manipulation' }}
          >
            <span className="flex items-center gap-1.5">
              <Network className="w-3.5 h-3.5" />
              Research happens in the Tech Tree — it leads to the space age.
            </span>
            <span className="shrink-0 font-semibold">Open →</span>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4" style={{ touchAction: 'pan-y' }}>
        {/* Active Projects */}
        {activeProjects.length > 0 && (
          <div>
            <button
              onClick={() => setActiveProjectsCollapsed((c) => !c)}
              className="w-full flex items-center justify-between mb-2 cursor-pointer select-none"
            >
              <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-yellow-400" />
                Active Projects ({activeProjects.length}/{maxSlots})
                {activeProjectsCollapsed ? (
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                )}
              </h3>
              <span className="text-xs text-slate-500">{availableSlots} slot{availableSlots !== 1 ? 's' : ''} free</span>
            </button>
            {!activeProjectsCollapsed && (
              <div className="space-y-2">
                {activeProjects.map((project) => (
                  <ActiveProjectCard
                    key={project.project_id}
                    project={project}
                    onCancel={handleCancel}
                    onComplete={handleComplete}
                    dayDurationMs={dayDurationMs}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1" style={{ touchAction: 'pan-x' }}>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`flex min-h-10 items-center gap-1.5 px-3 py-2 rounded-lg text-xs capitalize whitespace-nowrap transition-colors md:min-h-11 md:px-4 ${
                activeCategory === cat
                  ? 'bg-cyan-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              {cat !== 'all' && CATEGORY_ICONS[cat]}
              {cat}
            </button>
          ))}
        </div>

        {/* Available Projects */}
        {defsLoading ? (
          <div className="text-slate-400 text-sm">Loading projects...</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {filteredDefs.map((def) => {
              const canAfford = currentBalance >= def.cost;
              const slotsAvailable = availableSlots > 0;
              const isAvailable = def.unlocked && !def.purchased && canAfford && slotsAvailable && !def.hasConflict;
              const atMaxCount = def.maxCount !== undefined && def.existingCount >= def.maxCount;
              const requirementLines = getRequirementLines(def, currentBalance, availableSlots, stage);

              return (
                <div
                  key={def.upgrade_definition_id}
                  tabIndex={0}
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setInfoUpgradeId((current) => current === def.upgrade_definition_id ? null : current);
                    }
                  }}
                  className={`group relative flex min-h-[190px] flex-col p-3 rounded-xl border transition-all select-none focus:outline-none focus:ring-1 focus:ring-cyan-500/70 ${
                    def.purchased || atMaxCount
                      ? 'bg-green-950/30 border-green-800/50'
                      : def.unlocked && !def.hasConflict
                      ? canAfford && slotsAvailable
                        ? 'bg-slate-800/50 border-slate-700 hover:border-cyan-600/50 cursor-pointer'
                        : 'bg-slate-800/50 border-slate-700 opacity-70'
                      : 'bg-slate-900/50 border-slate-800 opacity-60'
                  }`}
                >
                  <div className="flex flex-1 items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm">{def.name}</h3>
                        {!def.unlocked && <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                        {def.purchased && <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />}
                        {def.hasConflict && (
                          <span className="text-[10px] bg-red-900/50 text-red-400 px-1.5 py-0.5 rounded">Conflict</span>
                        )}
                        {atMaxCount && (
                          <span className="text-[10px] bg-red-900/50 text-red-400 px-1.5 py-0.5 rounded">Max reached</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{def.description}</p>

                      {/* Trade-off tags */}
                      {def.tradeOffs.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {def.tradeOffs.map((tag) => (
                            <span
                              key={tag}
                              className={`text-[10px] px-1.5 py-0.5 rounded ${
                                TRADE_OFF_COLORS[tag] || 'text-slate-400 bg-slate-800'
                              }`}
                            >
                              {tag.startsWith('+') ? (
                                <TrendingUp className="w-2.5 h-2.5 inline mr-0.5" />
                              ) : tag.startsWith('-') ? (
                                <TrendingDown className="w-2.5 h-2.5 inline mr-0.5" />
                              ) : null}
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Construction info */}
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {def.construction_days}d build
                        </span>
                        {def.upkeep_cost > 0 && (
                          <span className="flex items-center gap-1">
                            <Zap className="w-3 h-3 text-yellow-500" />
                            {def.upkeep_cost}cr/day upkeep
                          </span>
                        )}
                      </div>

                      {/* Unlock reason */}
                      {def.unlockReason && (
                        <div className="flex items-center gap-1 mt-1">
                          <AlertCircle className="w-3 h-3 text-amber-500" />
                          <span className="text-[10px] text-amber-400">{def.unlockReason}</span>
                        </div>
                      )}
                      {!def.unlockReason && (def.prerequisiteNames.length > 0 || def.unlockRequirements.length > 0) && (
                        <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-500">
                          <AlertCircle className="w-3 h-3 text-slate-500" />
                          <span>Prerequisites met</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0 flex flex-col items-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoUpgradeId((current) => current === def.upgrade_definition_id ? null : def.upgrade_definition_id);
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-slate-300 hover:border-cyan-600 hover:text-cyan-300 focus:outline-none focus:ring-1 focus:ring-cyan-500/70"
                        aria-label={`Show requirements for ${def.name}`}
                      >
                        <Info className="h-4 w-4" />
                      </button>
                      <span className={`text-sm font-mono font-semibold ${canAfford ? 'text-yellow-400' : 'text-slate-500'}`}>
                        {def.cost}
                      </span>
                      <span className="text-[10px] text-slate-500">credits</span>
                      {isAvailable && !atMaxCount && (
                        <div className="flex flex-col gap-1 mt-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedUpgrade(def); }}
                            className="min-h-8 text-[11px] px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-cyan-300 transition-colors"
                            style={{ touchAction: 'manipulation' }}
                          >
                            Forecast
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleStart(def.upgrade_definition_id); }}
                            disabled={startProject.isPending}
                            className="min-h-8 text-[11px] px-2 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white transition-colors disabled:opacity-50"
                            style={{ touchAction: 'manipulation' }}
                          >
                            {startProject.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Start'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`absolute left-3 right-3 top-full z-30 mt-2 rounded-lg border border-cyan-700 bg-slate-950 p-2 shadow-2xl shadow-black/70 md:group-hover:block md:group-focus:block ${infoUpgradeId === def.upgrade_definition_id ? 'block' : 'hidden'} ${infoUpgradeId === def.upgrade_definition_id ? '' : 'pointer-events-none'}`}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-cyan-300">Requirements</div>
                    <div className="mt-1 space-y-0.5">
                      {requirementLines.map((line) => (
                        <div key={line} className="text-[10px] leading-snug text-slate-300">
                          {line}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Forecast Modal */}
      {selectedUpgrade && (
        <ForecastModal
          upgrade={selectedUpgrade}
          forecast={forecastData?.data}
          currentBalance={currentBalance}
          onStart={() => handleStart(selectedUpgrade.upgrade_definition_id)}
          onClose={() => setSelectedUpgrade(null)}
          isStarting={startProject.isPending}
        />
      )}
    </div>
  );
}
